import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { getApiOrigin } from "../../../../../infrastructure/utils/resolveUploadUrl";
import { ModalTransition } from "../../../../components/shared/Dialog/Config";
import {
  FinancialCoreTab,
  LendingOperationsTab,
  MehrsanOperationsTab,
  RequestActivityFeedTab,
  RequestInternalNotesTab,
  RequestFilesTab,
  RequestFollowUpsTab,
  RequestSummaryTab,
  RequestTechnicalInfoTab,
  RequestTimelineTab,
} from "../../../../components/request/case-tabs";
import { shouldShowLendingTab } from "../../../../components/request/case-tabs/lendingOperationsConstants";
import { shouldShowFinancialTab } from "../../../../components/request/case-tabs/financialCoreConstants";
import {
  isCaseWorkspaceOperationsApi,
  shouldShowMehrsanTab,
} from "../../../../components/request/case-tabs/mehrsanOperationsConstants";
import {
  collectRequestFiles,
  formatUpdateTypeLabel,
  parseAllRequestsDetail,
} from "./allRequestsPageUtils";
import { buildProjectWorkspacePath, openProjectWorkspacePathInNewTab, WORKSPACE_LIST_VIEWS } from "../../shared/projectWorkspace/projectWorkspacePaths";

const resolveWorkspaceConfigKey = (detailApi) =>
  String(detailApi || "").includes("Management") ? "management" : "solarentOperator";

const DEFAULT_DETAIL_API = "SLN/SolarentOperator/AllRequests";
const DEFAULT_HISTORY_API = "SLN/SolarentOperator/CheckNewRequest";

const AllRequestsCaseDrawer = ({
  open,
  requestId,
  listRow,
  onClose,
  detailApi = DEFAULT_DETAIL_API,
  workspaceConfigKey,
  historyApi = DEFAULT_HISTORY_API,
  listView = WORKSPACE_LIST_VIEWS.all,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const configKey = workspaceConfigKey || resolveWorkspaceConfigKey(detailApi);
  const showMehrsanTab = shouldShowMehrsanTab(detailApi);
  const showLendingTab = shouldShowLendingTab(detailApi);
  const showFinancialTab = shouldShowFinancialTab(detailApi);
  const showInternalNotesTab = isCaseWorkspaceOperationsApi(detailApi);

  let tabCursor = 1;
  const activitiesTabIndex = tabCursor++;
  const internalNotesTabIndex = showInternalNotesTab ? tabCursor++ : null;
  const historyTabIndex = tabCursor++;
  const followUpsTabIndex = tabCursor++;
  const filesTabIndex = tabCursor++;
  const technicalTabIndex = tabCursor++;
  const mehrsanTabIndex = showMehrsanTab ? tabCursor++ : null;
  const lendingTabIndex = showLendingTab ? tabCursor++ : null;
  const financialTabIndex = showFinancialTab ? tabCursor++ : null;

  const [tab, setTab] = useState(0);
  const [internalNotesRefreshKey, setInternalNotesRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadWarnings, setLoadWarnings] = useState([]);
  const [request, setRequest] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [history, setHistory] = useState([]);
  const [baseUrl, setBaseUrl] = useState("");

  const loadDetail = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setLoadWarnings([]);

    const warnings = [];
    let parsed = {
      request: null,
      followUps: [],
      history: [],
      baseUrl: "",
    };
    let historyParsed = { history: [], baseUrl: "" };

    try {
      const detailRes = await DefaultApiService.getOneApi(detailApi, requestId);
      if (detailRes?.isSuccess === false) {
        warnings.push("بارگذاری جزئیات پرونده ناموفق بود.");
      } else {
        parsed = parseAllRequestsDetail(detailRes);
      }
    } catch {
      warnings.push("بارگذاری جزئیات پرونده ناموفق بود.");
    }

    if (historyApi && historyApi !== detailApi) {
      try {
        const historyRes = await DefaultApiService.getOneApi(historyApi, requestId);
        if (historyRes?.isSuccess === false) {
          warnings.push("بارگذاری تاریخچه ناموفق بود.");
        } else {
          historyParsed = parseAllRequestsDetail(historyRes);
        }
      } catch {
        warnings.push("بارگذاری تاریخچه ناموفق بود.");
      }
    } else {
      historyParsed = { history: parsed.history ?? [], baseUrl: parsed.baseUrl };
    }

    const resolvedRequest = parsed.request || listRow || null;
    setRequest(resolvedRequest);
    setFollowUps(parsed.followUps ?? []);
    setHistory(
      historyParsed.history?.length
        ? historyParsed.history
        : parsed.history ?? []
    );
    setBaseUrl(
      parsed.baseUrl || historyParsed.baseUrl || getApiOrigin()
    );
    setLoadWarnings(warnings);

    if (!resolvedRequest) {
      toast.error("اطلاعات پرونده یافت نشد.");
    } else if (warnings.length) {
      toast.warning(
        warnings.length === 1 && listRow
          ? `${warnings[0]} نمایش خلاصه از لیست.`
          : warnings.join(" ")
      );
    }
    setLoading(false);
  }, [requestId, listRow, detailApi, historyApi]);

  useEffect(() => {
    if (!open || !requestId) return;
    setTab(0);
    loadDetail();
  }, [open, requestId, loadDetail]);

  const displayRequest = request || listRow;
  const files = useMemo(
    () => collectRequestFiles(displayRequest || {}),
    [displayRequest]
  );

  const hasCoords =
    displayRequest &&
    Number(displayRequest.xPoint ?? displayRequest.XPoint) !== 0 &&
    Number(displayRequest.yPoint ?? displayRequest.YPoint) !== 0;

  const mapCenter = hasCoords
    ? [
        Number(displayRequest.yPoint ?? displayRequest.YPoint),
        Number(displayRequest.xPoint ?? displayRequest.XPoint),
      ]
    : [32.68, 51.68];

  const titleName =
    displayRequest?.fullName ||
    displayRequest?.applicantFullName ||
    `${displayRequest?.firstName || ""} ${displayRequest?.lastName || ""}`.trim() ||
    "—";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={ModalTransition}
        fullWidth
        fullScreen={isMobile}
        maxWidth="lg"
        scroll="paper"
        sx={{ zIndex: theme.zIndex.modal }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            minHeight: isMobile ? "100dvh" : "min(560px, 85vh)",
            maxHeight: isMobile ? "100dvh" : "92vh",
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 0.5,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={800} noWrap fontSize="1.05rem">
                پرونده — {titleName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                کد {displayRequest?.referenceID ?? requestId ?? "—"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button
                size="small"
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={() => {
                  const path = buildProjectWorkspacePath(requestId, configKey, {
                    listView,
                  });
                  if (!path) {
                    toast.error("شناسه درخواست نامعتبر است.");
                    return;
                  }
                  openProjectWorkspacePathInNewTab(path);
                }}
              >
                پرونده کامل
              </Button>
              <IconButton aria-label="بستن" onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons="auto"
            sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.75 } }}
          >
            <Tab label="خلاصه" />
            <Tab label="فعالیت‌ها" />
            {showInternalNotesTab ? <Tab label="یادداشت‌های داخلی" /> : null}
            <Tab label="تاریخچه" />
            <Tab label="پیگیری‌ها" />
            <Tab label="فایل‌ها" />
            <Tab label="فنی" />
            {showMehrsanTab ? <Tab label="مهرسان" /> : null}
            {showLendingTab ? <Tab label="وام و تسهیلات" /> : null}
            {showFinancialTab ? <Tab label="مالی" /> : null}
          </Tabs>
        </Box>

        <DialogContent
          dividers
          sx={{
            pt: 1.5,
            pb: 2,
            flex: 1,
            minHeight: isMobile ? 280 : 360,
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : !displayRequest ? (
            <Alert severity="warning">اطلاعات پرونده یافت نشد.</Alert>
          ) : (
            <>
              {loadWarnings.length > 0 ? (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  {loadWarnings.join(" ")}
                  {listRow ? " برخی تب‌ها ممکن است ناقص باشند." : ""}
                </Alert>
              ) : null}
              {tab === 0 && (
                <RequestSummaryTab
                  request={displayRequest}
                  titleName={titleName}
                  listRow={listRow}
                />
              )}
              {tab === activitiesTabIndex && (
                <RequestActivityFeedTab
                  requestInfoId={requestId}
                  detailApi={detailApi}
                  fileBaseUrl={baseUrl}
                  active={open && tab === activitiesTabIndex}
                  history={history}
                  followUps={followUps}
                  internalNotesRefreshKey={internalNotesRefreshKey}
                />
              )}
              {showInternalNotesTab && tab === internalNotesTabIndex && (
                <RequestInternalNotesTab
                  requestInfoId={requestId}
                  detailApi={detailApi}
                  fileBaseUrl={baseUrl}
                  active={open && tab === internalNotesTabIndex}
                  onNotesChanged={() =>
                    setInternalNotesRefreshKey((k) => k + 1)
                  }
                />
              )}
              {tab === historyTabIndex && (
                <RequestTimelineTab
                  history={history}
                  formatUpdateTypeLabel={formatUpdateTypeLabel}
                />
              )}
              {tab === followUpsTabIndex && (
                <RequestFollowUpsTab followUps={followUps} />
              )}
              {tab === filesTabIndex && (
                <RequestFilesTab files={files} fileBaseUrl={baseUrl} />
              )}
              {tab === technicalTabIndex && (
                <RequestTechnicalInfoTab
                  request={displayRequest}
                  mapCenter={mapCenter}
                  hasCoords={hasCoords}
                />
              )}
              {mehrsanTabIndex != null && tab === mehrsanTabIndex && (
                <MehrsanOperationsTab
                  requestInfoId={requestId}
                  detailApi={detailApi}
                  fileBaseUrl={baseUrl}
                  active={open && tab === mehrsanTabIndex}
                />
              )}
              {lendingTabIndex != null && tab === lendingTabIndex && (
                <LendingOperationsTab
                  requestInfoId={requestId}
                  detailApi={detailApi}
                  fileBaseUrl={baseUrl}
                  active={open && tab === lendingTabIndex}
                />
              )}
              {financialTabIndex != null && tab === financialTabIndex && (
                <FinancialCoreTab
                  requestInfoId={requestId}
                  detailApi={detailApi}
                  fileBaseUrl={baseUrl}
                  active={open && tab === financialTabIndex}
                />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

AllRequestsCaseDrawer.propTypes = {
  open: PropTypes.bool,
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  listRow: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  detailApi: PropTypes.string,
  historyApi: PropTypes.string,
  workspaceConfigKey: PropTypes.string,
  listView: PropTypes.string,
};

export default AllRequestsCaseDrawer;
